<template>
  <div class="event-calendar">
    <!-- Calendar Header -->
    <div class="calendar-header d-flex align-center justify-space-between mb-6">
      <div class="d-flex align-center">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          @click="prevMonth"
        ></v-btn>
        <h2 class="text-h5 font-weight-black text-white mx-4 min-width-200 text-center">
          {{ monthName }} {{ currentYear }}
        </h2>
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          @click="nextMonth"
        ></v-btn>
      </div>
      
      <div class="d-flex align-center gap-2">
        <v-btn
          variant="outlined"
          rounded="pill"
          size="small"
          class="text-none font-weight-bold"
          @click="goToToday"
        >
          Today
        </v-btn>
        
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              icon="mdi-dots-vertical"
            ></v-btn>
          </template>
          <v-list class="bg-surface border border-white-05 pt-2 pb-2">
             <v-list-item 
               v-for="m in 12" 
               :key="m" 
               @click="selectMonth(m-1)"
               :active="currentMonth === m-1"
               density="compact"
             >
               <v-list-item-title>{{ getMonthName(m-1) }}</v-list-item-title>
             </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Calendar Grid -->
    <div class="calendar-grid border border-white-05 rounded-xl overflow-hidden bg-surface-variant-subtle">
      <!-- Days of Week Header -->
      <div class="days-header d-grid grid-cols-7 border-bottom border-white-05 bg-black-20">
        <div v-for="day in daysOfWeek" :key="day" class="pa-3 text-center text-caption font-weight-black text-grey-lighten-1 text-uppercase letter-spacing-1">
          {{ day }}
        </div>
      </div>

      <!-- Grid Cells -->
      <div class="days-grid d-grid grid-cols-7">
        <div 
          v-for="(day, index) in calendarDays" 
          :key="index" 
          class="calendar-cell pa-2 border-right border-bottom border-white-05"
          :class="{
            'other-month': !day.isCurrentMonth,
            'is-today': day.isToday,
            'has-events': day.events.length > 0
          }"
          style="min-height: 120px; min-width: 0; overflow: hidden;"
        >
          <div class="cell-header d-flex justify-space-between align-center mb-1">
            <span 
              class="day-number text-caption font-weight-bold d-flex align-center justify-center rounded-circle"
              :class="day.isToday ? 'bg-primary text-black' : 'text-grey-lighten-1'"
              style="width: 24px; height: 24px;"
            >
              {{ day.dayNumber }}
            </span>
          </div>
          
          <div class="cell-events overflow-hidden">
            <template v-for="event in day.events.slice(0, 3)" :key="event.uri">
              <div 
                class="mini-event-chip mb-1 pa-1 rounded px-2 text-tiny text-truncate cursor-pointer hover-rise"
                style="display: block; width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"
                :class="getEventChipClass(event, day.date)"
                @click="$emit('open-event', event)"
              >
                {{ event.name }}
              </div>
            </template>
            <div v-if="day.events.length > 3" class="text-tiny text-grey pl-2">
              +{{ day.events.length - 3 }} more
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Event } from '@/services/bluesky';

const props = defineProps<{
  events: Event[];
}>();

const emit = defineEmits(['open-event']);

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const currentDate = ref(new Date());
const currentMonth = computed(() => currentDate.value.getMonth());
const currentYear = computed(() => currentDate.value.getFullYear());

const monthName = computed(() => getMonthName(currentMonth.value));

const getMonthName = (month: number) => {
  return new Date(2000, month, 1).toLocaleString('en-US', { month: 'long' });
};

const prevMonth = () => {
  currentDate.value = new Date(currentYear.value, currentMonth.value - 1, 1);
};

const nextMonth = () => {
  currentDate.value = new Date(currentYear.value, currentMonth.value + 1, 1);
};

const goToToday = () => {
  currentDate.value = new Date();
};

const selectMonth = (month: number) => {
  currentDate.value = new Date(currentYear.value, month, 1);
};

const calendarDays = computed(() => {
  const year = currentYear.value;
  const month = currentMonth.value;
  
  // First day of the month
  const firstDayOfMonth = new Date(year, month, 1);
  const startDayOfWeek = firstDayOfMonth.getDay(); // 0 is Sunday
  
  // Last day of the month
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Previous month padding
  const lastDayOfPrevMonth = new Date(year, month, 0).getDate();
  
  const days = [];
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;


  // Fill in prefix days from previous month
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const d = lastDayOfPrevMonth - i;
    const dateStr = formatDate(year, month - 1, d);
    days.push({
      date: dateStr,
      dayNumber: d,
      isCurrentMonth: false,
      isToday: false,
      events: getEventsForDate(dateStr)
    });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = formatDate(year, month, d);
    days.push({
      date: dateStr,
      dayNumber: d,
      isCurrentMonth: true,
      isToday: dateStr === todayStr,
      events: getEventsForDate(dateStr)
    });
  }

  // Suffix days from next month to complete the grid (usually 42 cells for 6 weeks or 35 for 5)
  const remainingCells = 42 - days.length;
  for (let d = 1; d <= remainingCells; d++) {
    const dateStr = formatDate(year, month + 1, d);
    days.push({
      date: dateStr,
      dayNumber: d,
      isCurrentMonth: false,
      isToday: false,
      events: getEventsForDate(dateStr)
    });
  }

  return days;
});

const formatDate = (year: number, month: number, day: number) => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const getEventsForDate = (dateStr: string) => {
  return props.events.filter(event => {
    const start = event.date;
    const end = event.endDate || event.date;
    return dateStr >= start && dateStr <= end;
  }).sort((a, b) => {
      // Sort by time if available
      if (a.time && b.time) return a.time.localeCompare(b.time);
      if (a.time) return -1;
      if (b.time) return 1;
      return 0;
  });
};

const getEventChipClass = (event: Event, dayDate: string) => {
  const isStart = event.date === dayDate;
  const isEnd = (event.endDate || event.date) === dayDate;
  const isMultiDay = !!event.endDate && event.endDate !== event.date;
  
  return {
    'bg-primary-darken-1': !isMultiDay,
    'bg-indigo-darken-2': isMultiDay,
    'rounded-left-0': isMultiDay && !isStart,
    'rounded-right-0': isMultiDay && !isEnd,
    'opacity-80': !isStart && !isEnd
  };
};
</script>

<style scoped>
.calendar-grid {
  display: flex;
  flex-direction: column;
}

.grid-cols-7 {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-cell {
  position: relative;
  transition: background-color 0.2s ease;
}

.calendar-cell:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.calendar-cell.other-month {
  opacity: 0.3;
}

.calendar-cell.is-today {
  background-color: rgba(219, 168, 88, 0.03);
}

.mini-event-chip {
  font-size: 0.65rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.text-tiny {
  font-size: 0.6rem;
}

.min-width-200 {
  min-width: 200px;
}

.gap-2 {
  gap: 8px;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.bg-surface-variant-subtle {
  background-color: rgba(18, 18, 18, 0.4);
}

.bg-black-20 {
  background-color: rgba(0, 0, 0, 0.2);
}

.rounded-left-0 {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.rounded-right-0 {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.hover-rise:hover {
  transform: translateY(-1px);
  filter: brightness(1.2);
}
</style>
